<div class="main-view space-y-2 md:space-y-4">
    <app-page-surface-header titleIcon="clipboard"
        title="Session Detail"
        subtitle="Full record for a single PD session — KYC, transcript and scoring."
    />

    <div *ngIf="error" class="panel rounded-2xl p-4 text-sm text-red-600 dark:text-red-400">
        {{ error }}
        <button type="button" class="ml-2 underline" (click)="goBack()">Back to audit trail</button>
    </div>

    <div *ngIf="detailLoading && !detailSession" class="panel rounded-2xl p-10 text-center text-sm text-gray-400">
        Loading session…
    </div>

    <div *ngIf="detailSession"
        class="panel relative w-full overflow-hidden rounded-2xl !p-0">
        <!-- Modal header -->
        <div class="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-[#191e3a]">
            <div class="flex min-w-0 items-center gap-2.5">
                <div class="flex size-8 shrink-0 items-center justify-center rounded-full"
                    [class]="sessionType(detailSession) === 'KYC Bot' ? 'bg-violet-100 dark:bg-violet-950/50' : 'bg-sky-100 dark:bg-sky-950/50'">
                    <icon-user class="size-4"
                        [class]="sessionType(detailSession) === 'KYC Bot' ? 'text-violet-600 dark:text-violet-300' : 'text-sky-600 dark:text-sky-300'" />
                </div>
                <div class="min-w-0">
                    <h2 id="session-detail-title" class="truncate text-base font-bold text-gray-900 dark:text-white">
                        {{ agentName(detailSession) }}
                    </h2>
                    <p class="text-xs text-gray-400">{{ detailSession.session_code ?? ('Session #' + detailSession.id) }} · {{ dateDisplay(detailSession) }} {{ timeDisplay(detailSession) }}</p>
                </div>
            </div>
            <div class="flex shrink-0 items-center gap-2">
                <button
                    *ngIf="!showsPanel('kyc')"
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-[#0e1726] dark:text-gray-200 dark:hover:bg-white/5"
                    (click)="showKycTab()"
                >
                    <icon-eye class="size-3.5 shrink-0" />
                    KYC Details
                </button>
            </div>
        </div>

        <!-- Tab bar -->
        <div class="flex shrink-0 border-b border-gray-200 dark:border-[#191e3a]">
            <button *ngFor="let tab of detailTabs"
                type="button"
                class="px-5 py-3 text-sm font-medium transition-colors"
                [class]="activeDetailTab === tab.id
                    ? 'border-b-2 border-[#303030] text-gray-900 dark:border-white dark:text-white'
                    : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white'"
                (click)="activeDetailTab = $any(tab.id)"
            >
                {{ tab.label }}
                <span *ngIf="panelFor(tab.id) === 'transcript' && transcriptLog(detailSession).length"
                    class="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-white/10 dark:text-gray-300">
                    {{ transcriptLog(detailSession).length }}
                </span>
            </button>
        </div>

        <!-- Modal body -->
        <div class="p-5">

            <!-- ── KYC Details tab ── -->
            <div *ngIf="showsPanel('kyc')">
                <div>
                    <!-- The image tiles sit two across (2 x 2) in a wider right column, so the column is no longer
                         taller than the list beside it and the grey panel does not trail off into empty space. -->
                    <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_min(100%,34rem)] lg:items-start lg:gap-8">
                        <div class="rounded-xl bg-gray-100 p-4 dark:bg-white/[0.06]">
                            <div class="divide-y divide-gray-200 text-sm dark:divide-gray-600">
                                <div class="flex flex-col gap-1 py-2.5 first:pt-0">
                                    <span class="shrink-0 text-gray-500 dark:text-gray-400">Selected Records</span>
                                    <span class="font-medium text-gray-900 dark:text-white">{{ kycModalSelectedRecord }}</span>
                                </div>
                                <div class="flex flex-col gap-1 py-2.5">
                                    <span class="text-gray-500 dark:text-gray-400">Face Match</span>
                                    <span [class]="kycModalFaceMatchClass">{{ kycModalFaceMatch }}</span>
                                </div>
                                <div class="flex flex-col gap-1 py-2.5">
                                    <span class="text-gray-500 dark:text-gray-400">Liveness</span>
                                    <span [ngClass]="kycModalLivenessClass">{{ kycModalLiveness }}</span>
                                </div>
                                <div class="flex flex-col gap-1 py-2.5">
                                    <span class="text-gray-500 dark:text-gray-400">Aadhaar OTP</span>
                                    <ng-container *ngIf="kycAadhaarOtpApproved; else otpPending">
                                        <span class="inline-flex w-fit items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                                            <icon-square-check class="size-3.5 shrink-0" />
                                            Approved
                                        </span>
                                    </ng-container>
                                    <ng-template #otpPending>
                                        <span class="font-medium text-gray-400 dark:text-gray-500">Pending</span>
                                    </ng-template>
                                </div>
                                <div class="flex flex-col gap-1 py-2.5">
                                    <span class="text-gray-500 dark:text-gray-400">ID Document OCR</span>
                                    <ng-container *ngIf="kycModalPanOcrHasResult; else ocrNotRun">
                                        <span *ngIf="kycModalPanOcrMatched; else ocrMismatch"
                                            class="inline-flex w-fit items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                                            <icon-square-check class="size-3.5 shrink-0" />
                                            Matched
                                        </span>
                                        <ng-template #ocrMismatch>
                                            <span class="inline-flex w-fit items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800 dark:bg-red-950/60 dark:text-red-300">
                                                <icon-x class="size-3.5 shrink-0" />
                                                Mismatch
                                            </span>
                                        </ng-template>
                                    </ng-container>
                                    <ng-template #ocrNotRun>
                                        <span class="font-medium text-gray-400 dark:text-gray-500">Not run</span>
                                    </ng-template>
                                </div>
                                <div class="flex flex-col gap-1 py-2.5">
                                    <span class="text-gray-500 dark:text-gray-400">DOB</span>
                                    <span class="font-medium"
                                        [class]="kycModalDob === '—' ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'"
                                    >{{ kycModalDob }}</span>
                                </div>
                                <div class="flex flex-col gap-1 py-2.5">
                                    <span class="shrink-0 text-gray-500 dark:text-gray-400">Address (entered)</span>
                                    <span
                                        class="font-medium leading-snug whitespace-pre-line"
                                        [class]="kycEnteredAddress ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'"
                                    >{{ kycEnteredAddress ?? '—' }}</span>
                                    <span
                                        *ngIf="!kycEnteredAddress"
                                        class="text-xs text-slate-400 dark:text-slate-500"
                                    >No address answer recorded in the interview transcript.</span>
                                </div>
                                <div class="flex flex-col gap-1 py-2.5">
                                    <span class="shrink-0 text-gray-500 dark:text-gray-400">Address (captured)</span>
                                    <span
                                        class="font-medium leading-snug"
                                        [class]="kycCapturedAddress ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'"
                                    >{{ kycCapturedAddress ?? '—' }}</span>
                                    <span *ngIf="kycCapturedAddressError && !kycCapturedAddress" class="text-xs text-amber-600 dark:text-amber-400">
                                        ⚠ {{ kycCapturedAddressError }}
                                    </span>
                                    <span *ngIf="kycCapturedAddressError && kycCapturedAddress" class="text-xs text-amber-500 dark:text-amber-500">
                                        ⚠ Partial capture — some fields may be missing
                                    </span>
                                    <span
                                        *ngIf="!kycCapturedAddress && !kycCapturedAddressError"
                                        class="text-xs text-slate-400 dark:text-slate-500"
                                    >No address on file for this session (Aadhaar capture, uploaded Aadhaar, or address proof).</span>
                                </div>
                                <div class="flex flex-col gap-1 py-2.5 last:pb-0">
                                    <span class="text-gray-500 dark:text-gray-400">Location</span>
                                    <span *ngIf="kycGeoAddressLoading" class="text-sm text-gray-400 dark:text-gray-500">Looking up address…</span>
                                    <span
                                        *ngIf="!kycGeoAddressLoading && kycGeoAddress"
                                        class="font-medium leading-snug text-gray-900 dark:text-white"
                                    >{{ kycGeoAddress }}</span>
                                    <span
                                        class="font-medium"
                                        [class]="kycMapUrl ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'"
                                        [class.text-xs]="kycGeoAddress && !kycGeoAddressLoading"
                                        [class.font-normal]="kycGeoAddress && !kycGeoAddressLoading"
                                        [class.text-gray-500]="kycGeoAddress && !kycGeoAddressLoading"
                                        [class.dark:text-gray-400]="kycGeoAddress && !kycGeoAddressLoading"
                                    >{{ kycModalLatLng }}</span>
                                    <span
                                        *ngIf="!kycGeoAddressLoading && !kycGeoAddress && kycMapUrl"
                                        class="text-xs text-slate-400 dark:text-slate-500"
                                    >Address could not be resolved for these coordinates.</span>
                                </div>
                            </div>
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-600 dark:bg-[#0e1726]">
                                <div class="flex min-h-40 items-center justify-center overflow-hidden p-2 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/40 dark:to-amber-950/20">
                                    <img *ngIf="kycPanCardImage; else noDocImage"
                                        [src]="kycPanCardImage"
                                        alt="Captured identity document"
                                        class="aspect-square w-72 max-w-full rounded-lg border border-gray-200 bg-gray-100 object-contain dark:border-gray-700 dark:bg-gray-900"
                                    />
                                    <ng-template #noDocImage>
                                        <div class="px-4 text-center text-[10px] font-semibold uppercase tracking-wide text-amber-900/70 dark:text-amber-200/80">
                                            No capture yet<br /><span class="text-xs normal-case font-normal">Identity Document</span>
                                        </div>
                                    </ng-template>
                                </div>
                                <div class="border-t border-gray-100 bg-gray-50 px-3 py-2 text-center text-xs font-medium text-gray-600 dark:border-gray-600 dark:bg-white/5 dark:text-gray-400">
                                    Identity Document
                                </div>
                            </div>
                            <div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-600 dark:bg-[#0e1726]">
                                <div class="flex min-h-40 items-center justify-center overflow-hidden p-2 bg-gradient-to-b from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800">
                                    <img *ngIf="kycSelfieImage; else noSelfieImage"
                                        [src]="kycSelfieImage"
                                        alt="Live selfie"
                                        class="aspect-square w-72 max-w-full rounded-lg border border-gray-200 bg-gray-100 object-contain dark:border-gray-700 dark:bg-gray-900"
                                    />
                                    <ng-template #noSelfieImage>
                                        <icon-user class="size-16 text-slate-400 dark:text-slate-500" />
                                    </ng-template>
                                </div>
                                <div class="border-t border-gray-100 bg-gray-50 px-3 py-2 text-center text-xs font-medium text-gray-600 dark:border-gray-600 dark:bg-white/5 dark:text-gray-400">
                                    Live Photo
                                </div>
                            </div>
                            <div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-600 dark:bg-[#0e1726]">
                                <div class="flex min-h-40 items-center justify-center overflow-hidden p-2 bg-gradient-to-b from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800">
                                    <img *ngIf="kycAadhaarEkycImage; else noAadhaarEkycImage"
                                        [src]="kycAadhaarEkycImage"
                                        alt="Aadhaar e-KYC"
                                        class="aspect-square w-72 max-w-full rounded-lg border border-gray-200 bg-gray-100 object-contain dark:border-gray-700 dark:bg-gray-900"
                                    />
                                    <ng-template #noAadhaarEkycImage>
                                        <div class="px-4 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            No Aadhaar photo<br /><span class="text-xs normal-case font-normal">Not stored on this session</span>
                                        </div>
                                    </ng-template>
                                </div>
                                <div class="border-t border-gray-100 bg-gray-50 px-3 py-2 text-center text-xs font-medium text-gray-600 dark:border-gray-600 dark:bg-white/5 dark:text-gray-400">
                                    Aadhaar e-KYC
                                </div>
                            </div>
                            <div class="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-600 dark:bg-[#0e1726]">
                                <div class="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-slate-200 dark:bg-slate-700">
                                    <iframe *ngIf="kycMapUrl; else noMapImage"
                                        [src]="kycMapUrl"
                                        class="h-full w-full border-0"
                                        title="Geo location map"
                                        loading="lazy"
                                        allowfullscreen
                                    ></iframe>
                                    <ng-template #noMapImage>
                                        <div class="absolute inset-0 opacity-40"
                                            style="background-image: linear-gradient(#94a3b8 1px, transparent 1px), linear-gradient(90deg, #94a3b8 1px, transparent 1px); background-size: 20px 20px;"
                                        ></div>
                                        <icon-map-pin class="relative z-[1] size-10 text-red-500 drop-shadow-md" />
                                    </ng-template>
                                </div>
                                <div class="border-t border-gray-100 bg-gray-50 px-3 py-2 text-center text-xs font-medium text-gray-600 dark:border-gray-600 dark:bg-white/5 dark:text-gray-400">
                                    Location
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Captured vs Uploaded comparison + Compare Again -->
                    <div class="mt-4"
                        *ngIf="detailSession.ai_response?.pan || detailSession.ai_response?.face">
                        <h3 class="mb-3 text-sm font-bold text-gray-900 dark:text-white">
                            Identity Verification — Captured vs Uploaded
                        </h3>
                        <app-kyc-capture-comparison
                            [sessionId]="detailSession.id"
                            [tenantId]="detailSession.tenant_id"
                            [pan]="detailSession.ai_response?.pan ?? null"
                            [face]="detailSession.ai_response?.face ?? null"
                            (sessionUpdated)="onKycSessionUpdated($event)"></app-kyc-capture-comparison>
                    </div>
                </div>
            </div>

            <!-- ── Overview tab ── -->
            <div *ngIf="showsPanel('overview')" class="space-y-4">
                <!-- Result hero -->
                <div class="flex items-center justify-between rounded-xl px-4 py-3"
                    [class]="detailSession.result === 'PASS' ? 'bg-emerald-50 dark:bg-emerald-950/30'
                           : detailSession.result === 'FAIL' ? 'bg-red-50 dark:bg-red-950/30'
                           : detailSession.result === 'REVIEW' ? 'bg-amber-50 dark:bg-amber-950/30'
                           : 'bg-gray-50 dark:bg-white/5'">
                    <div>
                        <p class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Result</p>
                        <p class="text-2xl font-bold mt-0.5"
                            [class]="detailSession.result === 'PASS' ? 'text-emerald-700 dark:text-emerald-300'
                                   : detailSession.result === 'FAIL' ? 'text-red-700 dark:text-red-300'
                                   : detailSession.result === 'REVIEW' ? 'text-amber-700 dark:text-amber-200'
                                   : 'text-gray-600 dark:text-gray-300'">
                            {{ detailSession.result ?? 'N/A' }}
                        </p>
                    </div>
                    <div class="text-right">
                        <p class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Score</p>
                        <p class="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">{{ scoreDisplay(detailSession) }}<span class="text-sm font-normal text-gray-400">/100</span></p>
                    </div>
                </div>

                <!-- Fields -->
                <div class="rounded-xl bg-gray-50 p-4 dark:bg-white/[0.04]">
                    <div class="divide-y divide-gray-200 text-sm dark:divide-gray-700">
                        <div class="flex justify-between gap-4 py-2.5 first:pt-0">
                            <span class="text-gray-500 dark:text-gray-400">Type</span>
                            <span class="font-medium text-gray-900 dark:text-white">{{ sessionType(detailSession) }}</span>
                        </div>
                        <div class="flex justify-between gap-4 py-2.5">
                            <span class="text-gray-500 dark:text-gray-400">Event</span>
                            <span class="font-medium text-gray-900 dark:text-white">{{ eventLabel(detailSession) }}</span>
                        </div>
                        <div class="flex justify-between gap-4 py-2.5">
                            <span class="text-gray-500 dark:text-gray-400">Customer</span>
                            <span class="font-medium text-gray-900 dark:text-white">{{ detailSession.customer_name || '—' }}</span>
                        </div>
                        <div class="flex justify-between gap-4 py-2.5">
                            <span class="text-gray-500 dark:text-gray-400">Loan Type</span>
                            <span class="font-medium text-gray-900 dark:text-white">{{ detailSession.loan_type || '—' }}</span>
                        </div>
                        <div class="flex justify-between gap-4 py-2.5">
                            <span class="text-gray-500 dark:text-gray-400">Runtime</span>
                            <span class="font-mono font-medium text-gray-900 dark:text-white">{{ runtimeDisplay(detailSession) }}</span>
                        </div>
                        <div class="flex justify-between gap-4 py-2.5">
                            <span class="text-gray-500 dark:text-gray-400">Geo Location</span>
                            <span class="font-mono text-sm font-medium text-gray-900 dark:text-white">{{ geoDisplay(detailSession) }}</span>
                        </div>
                        <!-- KYC-specific fields -->
                        <ng-container *ngIf="detailSession.ai_response?.pan">
                            <div class="flex justify-between gap-4 py-2.5">
                                <span class="text-gray-500 dark:text-gray-400">Face Match</span>
                                <span class="font-medium"
                                    [class]="detailSession.ai_response?.face?.matched ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'">
                                    {{ detailSession.ai_response?.face?.score != null ? (detailSession.ai_response!.face!.score | number:'1.0-0') + '%' : '—' }}
                                    ({{ detailSession.ai_response?.face?.matched ? 'Matched' : 'Not matched' }})
                                </span>
                            </div>
                            <div class="flex justify-between gap-4 py-2.5">
                                <span class="text-gray-500 dark:text-gray-400">Liveness</span>
                                <span class="font-medium"
                                    [class]="detailSession.ai_response?.face?.liveness_verified ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'">
                                    {{ detailSession.ai_response?.face?.liveness_score != null ? (detailSession.ai_response!.face!.liveness_score | number:'1.0-0') + '%' : '—' }}
                                    ({{ detailSession.ai_response?.face?.liveness_verified ? 'Live' : 'Not live' }})
                                </span>
                            </div>
                            <div class="flex justify-between gap-4 py-2.5">
                                <span class="text-gray-500 dark:text-gray-400">ID OCR</span>
                                <span class="font-medium"
                                    [class]="detailSession.ai_response?.pan?.matched ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'">
                                    {{ detailSession.ai_response?.pan?.captured_number || '—' }}
                                    ({{ detailSession.ai_response?.pan?.matched ? 'Matched' : 'Mismatch' }})
                                </span>
                            </div>
                            <div class="flex justify-between gap-4 py-2.5">
                                <span class="text-gray-500 dark:text-gray-400">Captured DOB</span>
                                <span class="font-medium text-gray-900 dark:text-white">{{ detailSession.ai_response?.pan?.date_of_birth || '—' }}</span>
                            </div>
                        </ng-container>

                        <!-- KYC captured images -->
                        <ng-container *ngIf="detailSession.ai_response?.pan?.captured_image || detailSession.ai_response?.face?.captured_image">
                            <div class="py-2.5">
                                <span class="text-gray-500 dark:text-gray-400 text-sm">Captured Images</span>
                                <div class="mt-2 grid max-w-lg grid-cols-2 gap-3">
                                    <div *ngIf="detailSession.ai_response?.face?.captured_image" class="flex flex-col gap-1.5">
                                        <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Live Selfie</span>
                                        <div class="flex justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-[#191e3a] dark:bg-black/20">
                                            <img [src]="detailSession.ai_response!.face!.captured_image"
                                                alt="Captured selfie"
                                                class="aspect-square w-72 max-w-full object-contain" />
                                        </div>
                                        <span class="text-center text-[10px]"
                                            [class]="detailSession.ai_response?.face?.matched ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                                            {{ detailSession.ai_response?.face?.matched ? '✓ Face matched' : '✗ Not matched' }}
                                            · {{ detailSession.ai_response?.face?.score != null ? (detailSession.ai_response!.face!.score | number:'1.0-0') + '%' : '' }}
                                        </span>
                                    </div>
                                    <div *ngIf="detailSession.ai_response?.pan?.captured_image" class="flex flex-col gap-1.5">
                                        <span class="text-xs font-medium text-gray-500 dark:text-gray-400">PAN Card Capture</span>
                                        <div class="flex justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-[#191e3a] dark:bg-black/20">
                                            <img [src]="detailSession.ai_response!.pan!.captured_image"
                                                alt="Captured PAN card"
                                                class="aspect-square w-72 max-w-full object-contain" />
                                        </div>
                                        <span class="text-center text-[10px]"
                                            [class]="detailSession.ai_response?.pan?.matched ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                                            {{ detailSession.ai_response?.pan?.matched ? '✓ PAN matched' : '✗ Mismatch' }}
                                            · {{ detailSession.ai_response?.pan?.captured_number || '' }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </ng-container>

                        <!-- Flags -->
                        <div *ngIf="detailSession.ai_response?.flags?.length" class="flex flex-col gap-2 py-2.5 last:pb-0">
                            <span class="text-gray-500 dark:text-gray-400">Risk Flags</span>
                            <div class="flex flex-wrap gap-1.5">
                                <span *ngFor="let flag of detailSession.ai_response?.flags"
                                    class="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-950/50 dark:text-red-300">
                                    {{ flag }}
                                </span>
                            </div>
                        </div>
                        <!-- Summary -->
                        <div *ngIf="detailSession.ai_response?.summary" class="flex flex-col gap-1 py-2.5 last:pb-0">
                            <span class="text-gray-500 dark:text-gray-400">AI Summary</span>
                            <p class="text-sm leading-relaxed text-gray-800 dark:text-gray-200">{{ detailSession.ai_response?.summary }}</p>
                        </div>
                    </div>
                </div>

                <!-- ── Live Video Room ─────────────────────────────────────── -->
                <!-- Only for a session that had (or is set up for) an interview: a KYC-only workflow has no video step. -->
                <div *ngIf="sessionHadInterview(detailSession, sections)" class="rounded-xl border border-gray-200 bg-white p-4 dark:border-[#191e3a] dark:bg-[#0e1726]">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2">
                            <span class="flex size-7 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-950/50">
                                <svg class="size-4 text-indigo-600 dark:text-indigo-400" viewBox="0 0 24 24" fill="currentColor"><path d="M15 10l4.553-2.277A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/></svg>
                            </span>
                            <span class="text-sm font-semibold text-gray-800 dark:text-gray-100">Live Video Interview</span>
                        </div>
                        <button *ngIf="!showVideoEmbed" type="button"
                            class="btn rounded-full bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                            [disabled]="videoLoading"
                            (click)="startVideoRoom()">
                            {{ videoLoading ? 'Creating room…' : (videoRoom ? 'Reconnect' : 'Start Video') }}
                        </button>
                        <button *ngIf="showVideoEmbed" type="button"
                            class="btn rounded-full border border-gray-300 px-4 py-1.5 text-xs font-semibold dark:border-[#191e3a]"
                            (click)="closeVideoEmbed()">
                            Close Video
                        </button>
                    </div>

                    <!-- Existing room badge (from session metadata) -->
                    <div *ngIf="detailSession.metadata?.video_room_url && !videoRoom" class="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span class="size-1.5 rounded-full bg-emerald-400"></span>
                        Room already created for this session
                    </div>

                    <p *ngIf="videoError" class="mt-2 text-xs text-red-600 dark:text-red-400">{{ videoError }}</p>
                    <p *ngIf="recordingSaveError" class="mt-2 text-xs text-red-600 dark:text-red-400">{{ recordingSaveError }}</p>
                    <p *ngIf="recordingSaving" class="mt-2 text-xs text-amber-700 dark:text-amber-300">Saving interview recording…</p>

                    <!-- Saved recording playback -->
                    <div *ngIf="hasSavedRecording(detailSession) && !showVideoEmbed" class="mt-3 space-y-2">
                        <p class="text-xs font-medium text-gray-600 dark:text-gray-300">Saved recording</p>
                        <div *ngIf="recordingLoading" class="text-xs text-gray-400">Loading recording…</div>
                        <video
                            *ngIf="recordingPlaybackUrl"
                            class="w-full rounded-lg border border-gray-200 bg-black dark:border-[#191e3a]"
                            [src]="recordingPlaybackUrl"
                            controls
                            playsinline
                        >
                            <track kind="captions" label="English" srclang="en" default />
                        </video>
                    </div>

                    <!-- Room tokens (customer app joins with guest token + same LiveKit URL) -->
                    <div *ngIf="videoRoom?.is_configured && !showVideoEmbed" class="mt-3 space-y-2 text-xs">
                        <div class="rounded-lg bg-gray-50 px-3 py-2 dark:bg-white/[0.04]">
                            <span class="text-gray-500">LiveKit URL</span>
                            <p class="mt-0.5 select-all font-mono text-[10px] text-gray-700 dark:text-gray-300">{{ livekitServerUrl }}</p>
                        </div>
                        <div class="rounded-lg bg-gray-50 px-3 py-2 dark:bg-white/[0.04]">
                            <span class="text-gray-500">Room</span>
                            <p class="mt-0.5 font-mono text-[10px] text-gray-700 dark:text-gray-300">{{ videoRoom!.room_name }}</p>
                        </div>
                        <div class="rounded-lg bg-gray-50 px-3 py-2 dark:bg-white/[0.04]">
                            <span class="text-gray-500">Customer guest token</span>
                            <p class="mt-0.5 max-h-16 overflow-y-auto select-all font-mono text-[10px] text-gray-700 dark:text-gray-300">{{ videoRoom!.guest_token }}</p>
                        </div>
                    </div>

                    <!-- Embedded LiveKit call -->
                    <div *ngIf="showVideoEmbed && livekitHostToken" class="relative mt-3 overflow-hidden rounded-xl border border-gray-200 bg-gray-900 dark:border-[#191e3a]" style="height: 480px;">
                        <app-livekit-room
                            #livekitRoom
                            class="block size-full"
                            [serverUrl]="livekitServerUrl"
                            [token]="livekitHostToken"
                            [active]="livekitActive"
                            (transcript)="onAuditLivekitTranscript($event)"
                            (connectionError)="videoError = $event"
                            (sessionEnded)="onLivekitSessionEnded()"
                        />
                    </div>
                </div>
            </div>

            <!-- ── Transcript tab ── -->
            <div *ngIf="showsPanel('transcript')">
                <div *ngIf="!transcriptLog(detailSession).length" class="flex flex-col items-center gap-3 py-10 text-center">
                    <icon-notes class="size-10 text-gray-300 dark:text-gray-600" />
                    <p class="text-sm text-gray-400">No conversation log recorded for this session.</p>
                </div>
                <div *ngIf="transcriptLog(detailSession).length" class="space-y-3">
                    <div *ngFor="let msg of transcriptLog(detailSession)"
                        class="flex gap-3"
                        [class.flex-row-reverse]="msg.role === 'customer'"
                    >
                        <div class="flex size-7 shrink-0 items-center justify-center rounded-full text-white text-xs font-semibold"
                            [class]="msg.role === 'ai' ? 'bg-[#4a3882]' : 'bg-gray-400 dark:bg-gray-600'">
                            {{ msg.role === 'ai' ? 'AI' : 'C' }}
                        </div>
                        <div class="max-w-[78%]">
                            <div class="rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed"
                                [class]="msg.role === 'ai'
                                    ? 'rounded-tl-sm bg-[#f3f0ff] text-gray-800 dark:bg-[#4a3882]/30 dark:text-gray-100'
                                    : 'rounded-tr-sm bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-gray-100'">
                                {{ msg.text }}
                            </div>
                            <p class="mt-1 text-[10px] text-gray-400" [class.text-right]="msg.role === 'customer'">{{ conversationTimeDisplay(msg.time) }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ── Scoring tab ── -->
            <div *ngIf="showsPanel('scoring')">

                <!-- KYC scoring breakdown -->
                <ng-container *ngIf="detailSession.ai_response?.pan || detailSession.ai_response?.face">
                    <!-- Composite score header -->
                    <div class="mb-4 flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-[#191e3a] dark:bg-white/[0.03]">
                        <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">Composite KYC Score</span>
                        <span class="rounded-full px-3 py-0.5 text-sm font-bold"
                            [class]="(detailSession.ai_response?.composite_score ?? 0) >= 70
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                                : (detailSession.ai_response?.composite_score ?? 0) >= 40
                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-200'
                                    : 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'">
                            {{ detailSession.ai_response?.composite_score ?? 0 }}/100
                        </span>
                    </div>

                    <div class="space-y-3">
                        <!-- ID Document (PAN) -->
                        <div *ngIf="detailSession.ai_response?.pan" class="rounded-xl border border-gray-200 p-4 dark:border-[#191e3a]">
                            <div class="flex items-center justify-between gap-3">
                                <p class="text-sm font-semibold text-gray-800 dark:text-white">ID Document (PAN OCR)</p>
                                <span class="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold"
                                    [class]="detailSession.ai_response!.pan!.score >= 70
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                                        : 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'">
                                    {{ detailSession.ai_response!.pan!.score }}/100
                                </span>
                            </div>
                            <div class="mt-3 space-y-1.5 text-xs">
                                <div class="flex justify-between">
                                    <span class="text-gray-500">Captured PAN</span>
                                    <span class="font-mono font-medium text-gray-800 dark:text-gray-200">{{ detailSession.ai_response!.pan!.captured_number || '—' }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-500">Uploaded PAN</span>
                                    <span class="font-mono font-medium text-gray-800 dark:text-gray-200">{{ detailSession.ai_response!.pan!.uploaded_number || '—' }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-500">Name</span>
                                    <span class="font-medium text-gray-800 dark:text-gray-200">{{ detailSession.ai_response!.pan!.captured_name || '—' }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-500">Date of Birth</span>
                                    <span class="font-medium text-gray-800 dark:text-gray-200">{{ detailSession.ai_response!.pan!.date_of_birth || '—' }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-500">Match result</span>
                                    <span class="font-semibold"
                                        [class]="detailSession.ai_response!.pan!.matched ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'">
                                        {{ detailSession.ai_response!.pan!.matched ? '✓ Matched' : '✗ Mismatch' }}
                                    </span>
                                </div>
                                <p *ngIf="detailSession.ai_response!.pan!.error" class="mt-1 text-amber-600 dark:text-amber-400">⚠ {{ detailSession.ai_response!.pan!.error }}</p>
                            </div>
                        </div>

                        <!-- Face Match -->
                        <div *ngIf="detailSession.ai_response?.face" class="rounded-xl border border-gray-200 p-4 dark:border-[#191e3a]">
                            <div class="flex items-center justify-between gap-3">
                                <p class="text-sm font-semibold text-gray-800 dark:text-white">Face Match</p>
                                <span class="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold"
                                    [class]="detailSession.ai_response!.face!.score >= 70
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                                        : detailSession.ai_response!.face!.score >= 40
                                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-200'
                                            : 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'">
                                    {{ detailSession.ai_response!.face!.score }}/100
                                </span>
                            </div>
                            <div class="mt-3 space-y-1.5 text-xs">
                                <div class="flex justify-between">
                                    <span class="text-gray-500">Similarity</span>
                                    <span class="font-medium text-gray-800 dark:text-gray-200">{{ detailSession.ai_response!.face!.score }}%</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-500">Confidence</span>
                                    <span class="font-medium text-gray-800 dark:text-gray-200">{{ detailSession.ai_response!.face!.confidence }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-500">Match result</span>
                                    <span class="font-semibold"
                                        [class]="detailSession.ai_response!.face!.matched ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'">
                                        {{ detailSession.ai_response!.face!.matched ? '✓ Matched' : '✗ No match' }}
                                    </span>
                                </div>
                                <p *ngIf="detailSession.ai_response!.face!.error" class="mt-1 text-amber-600 dark:text-amber-400">⚠ {{ detailSession.ai_response!.face!.error }}</p>
                            </div>
                        </div>

                        <!-- Liveness -->
                        <div *ngIf="detailSession.ai_response?.face?.liveness_score != null" class="rounded-xl border border-gray-200 p-4 dark:border-[#191e3a]">
                            <div class="flex items-center justify-between gap-3">
                                <p class="text-sm font-semibold text-gray-800 dark:text-white">Liveness Detection</p>
                                <span class="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold"
                                    [class]="detailSession.ai_response!.face!.liveness_verified
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                                        : 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'">
                                    {{ detailSession.ai_response!.face!.liveness_score }}/100
                                </span>
                            </div>
                            <div class="mt-3 space-y-1.5 text-xs">
                                <div class="flex justify-between">
                                    <span class="text-gray-500">Result</span>
                                    <span class="font-semibold"
                                        [class]="detailSession.ai_response!.face!.liveness_verified ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'">
                                        {{ detailSession.ai_response!.face!.liveness_verified ? '✓ Live person detected' : '✗ Not live' }}
                                    </span>
                                </div>
                                <p *ngIf="detailSession.ai_response!.face!.liveness_error" class="mt-1 text-amber-600 dark:text-amber-400">⚠ {{ detailSession.ai_response!.face!.liveness_error }}</p>
                            </div>
                        </div>
                    </div>
                </ng-container>

                <!-- PD Q&A scoring -->
                <ng-container *ngIf="detailSession.ai_response?.answer_scores?.length">
                    <div class="space-y-3">
                        <div *ngFor="let qa of detailSession.ai_response?.answer_scores; let i = index"
                            class="rounded-xl border border-gray-200 p-4 dark:border-[#191e3a]">
                            <div class="flex items-start justify-between gap-3">
                                <p class="text-sm font-medium text-gray-800 dark:text-white">Q{{ i + 1 }}. {{ qa.question }}</p>
                                <span class="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold"
                                    [class]="qa.score >= 70 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                                           : qa.score >= 40 ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-200'
                                           : 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300'">
                                    {{ qa.score }}/100
                                </span>
                            </div>
                            <p class="mt-2 text-sm text-gray-600 dark:text-gray-300"><span class="font-medium text-gray-500 dark:text-gray-400">Answer: </span>{{ qa.answer || '—' }}</p>
                            <p class="mt-1.5 text-xs italic text-gray-400 dark:text-gray-500">{{ qa.reasoning }}</p>
                        </div>
                    </div>
                </ng-container>

                <!-- Empty state — only shown when neither KYC nor Q&A data present -->
                <div *ngIf="!detailSession.ai_response?.pan && !detailSession.ai_response?.face && !detailSession.ai_response?.answer_scores?.length"
                    class="flex flex-col items-center gap-3 py-10 text-center">
                    <icon-dollar-sign-circle class="size-10 text-gray-300 dark:text-gray-600" />
                    <p class="text-sm text-gray-400">No scoring data available for this session.</p>
                </div>
            </div>

    </div>
</div>
